import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import PreviewBanner from '../../components/rpat/PreviewBanner';
import MiniCommunicationScoreCard from '../../components/communication/MiniCommunicationScoreCard';
import { 
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Check,
  RefreshCw,
  Play,
  Loader2,
  Target,
  ClipboardList,
  Award,
  Shuffle,
  Lock,
  AlertCircle
} from 'lucide-react';

const RPATJobMastery = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [swapping, setSwapping] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [retakeJobId, setRetakeJobId] = useState(null);

  // Check for retake query param
  useEffect(() => {
    const retake = searchParams.get('retake');
    if (retake) {
      setRetakeJobId(retake);
      toast.info(`Retaking job: Scroll down to find the highlighted job`, {
        duration: 5000
      });
    }
  }, [searchParams]);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/status');
      setStatus(res.data);
      
      // Admin can always access
      const isAdmin = user?.role === 'admin';
      
      // Check if Phase 4 is complete
      if (!isAdmin && !res.data.phase_4_completed) {
        setIsPreviewMode(true);
      }
      
      // If no category selected and not admin, show toast but allow preview
      if (!res.data.selected_job_category && !isAdmin) {
        setIsPreviewMode(true);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
      toast.error('Failed to load job mastery data');
    } finally {
      setLoading(false);
    }
  }, [api, user?.role]);

  // Force sync progress from Job Simulations module
  const handleForceSync = async () => {
    try {
      setSyncing(true);
      const res = await api.post('/rpat/certification/force-sync');
      
      if (res.data.total_synced > 0) {
        toast.success(`Synced ${res.data.total_synced} completion(s)!`, {
          description: 'Your progress has been updated.'
        });
      } else {
        toast.info('Progress is already up to date', {
          description: 'No new completions to sync.'
        });
      }
      
      // Refresh status after sync
      await fetchStatus();
    } catch (err) {
      console.error('Failed to sync:', err);
      toast.error('Failed to sync progress. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSwapJob = async (jobId) => {
    try {
      setSwapping(jobId);
      await api.post('/rpat/certification/swap-job', { job_id: jobId });
      toast.success('Job swapped successfully');
      fetchStatus();
    } catch (err) {
      console.error('Failed to swap job:', err);
      toast.error(err.response?.data?.detail || 'Failed to swap job');
    } finally {
      setSwapping(null);
    }
  };

  const handleStartJob = (job) => {
    // Navigate to the job simulation detail page
    navigate(`/job-simulations/${job.id}`);
  };

  const handleCompleteJob = async (jobId, score = 85) => {
    try {
      const res = await api.post(`/rpat/certification/complete-job/${jobId}`, { score });
      toast.success(res.data.message);
      
      if (res.data.phase_5_complete) {
        toast.success('Phase 5 Complete! Phase 6 is now available.', {
          duration: 5000,
          action: {
            label: 'Go to Phase 6',
            onClick: () => navigate('/rpat/certification/sourcing-labs')
          }
        });
      }
      
      fetchStatus();
    } catch (err) {
      console.error('Failed to complete job:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete job');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
        </div>
      </MainLayout>
    );
  }

  const phase5 = status?.phases?.phase_5 || {};
  const jobs = phase5.jobs || [];
  const completedCount = phase5.completed_jobs || 0;
  const progressPct = (completedCount / 5) * 100;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/rpat')}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to R-PAT
            </Button>
          </div>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <PreviewBanner 
            message={!status?.phase_4_completed 
              ? 'Complete Phase 4 to unlock this phase.'
              : 'Select a job category from the R-PAT dashboard to begin.'}
            actionLabel="Go to Dashboard"
            onAction={() => navigate('/rpat')}
          />
        )}

        {/* Title Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              Phase 5: Job Category Mastery
            </h1>
            <p className="text-muted-foreground mt-1">
              Complete 5 job simulations in <span className="font-medium text-violet-600">{status?.selected_job_category_name || 'your selected category'}</span>
            </p>
          </div>
          <Badge 
            className={`text-base px-4 py-2 ${
              phase5.status === 'completed' ? 'bg-emerald-500' : 'bg-violet-500'
            }`}
          >
            {completedCount}/5 Completed
          </Badge>
        </div>

        {/* Progress Card */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Phase 5 Progress</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleForceSync}
                  disabled={syncing || loading}
                  className="text-xs"
                  data-testid="sync-progress-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  {syncing ? 'Syncing...' : 'Sync Progress'}
                </Button>
                <span className="text-sm text-muted-foreground">{Math.round(progressPct)}%</span>
              </div>
            </div>
            <Progress value={progressPct} className="h-3" />
            {phase5.status === 'completed' && (
              <div className="flex items-center gap-2 mt-3 text-emerald-600">
                <Check className="h-5 w-5" />
                <span className="font-medium">Phase 5 Complete! Proceed to Phase 6.</span>
                <Button 
                  size="sm" 
                  className="ml-auto bg-emerald-500"
                  onClick={() => navigate('/rpat/certification/sourcing-labs')}
                >
                  Go to Phase 6
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
            {/* Sync hint for users with incomplete progress */}
            {phase5.status !== 'completed' && completedCount < 5 && (
              <p className="text-xs text-muted-foreground mt-2">
                Completed jobs not showing? Click &quot;Sync Progress&quot; to update from Job Simulations module.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Communication Score */}
        <MiniCommunicationScoreCard moduleName="job_simulations" />

        {/* Jobs List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Your 5 Job Simulations</h2>
          <p className="text-sm text-muted-foreground">
            Complete each job&apos;s full simulation including job analysis, candidate screening, and assessment. 
            {status?.external_completions?.job_simulations?.completed_count > 0 && (
              <span className="text-emerald-600 font-medium ml-1">
                You have {status.external_completions.job_simulations.completed_count} jobs already completed from previous sessions!
              </span>
            )}
          </p>

          <div className="grid gap-4">
            {jobs.map((job, index) => {
              const isCompleted = job.status === 'completed';
              const completedExternally = job.completed_externally;
              const canSwap = !isCompleted && !completedExternally;
              const isRetakeTarget = retakeJobId === job.id;
              const needsImprovement = isCompleted && job.score < 70;

              return (
                <Card 
                  key={job.id}
                  id={`job-${job.id}`}
                  className={`transition-all ${
                    isRetakeTarget
                      ? 'ring-2 ring-amber-500 bg-amber-50 border-amber-300'
                      : needsImprovement
                        ? 'bg-red-50 border-red-200'
                        : isCompleted 
                          ? completedExternally 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-emerald-50 border-emerald-200'
                          : 'hover:shadow-md hover:border-violet-300'
                  }`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      {/* Number Badge */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isCompleted 
                          ? completedExternally 
                            ? 'bg-blue-500 text-white'
                            : 'bg-emerald-500 text-white' 
                          : 'bg-violet-100 text-violet-600'
                      }`}>
                        {isCompleted ? <Check className="h-5 w-5" /> : index + 1}
                      </div>

                      {/* Job Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{job.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {job.difficulty}
                          </Badge>
                          {completedExternally && (
                            <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                              Already Completed
                            </Badge>
                          )}
                          {isRetakeTarget && (
                            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Needs Improvement
                            </Badge>
                          )}
                          {needsImprovement && !isRetakeTarget && (
                            <Badge className="text-xs bg-red-100 text-red-700 border-red-300">
                              Below 70%
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {job.description}
                        </p>
                        {isCompleted && (
                          <p className={`text-xs mt-1 ${
                            needsImprovement 
                              ? 'text-red-600' 
                              : completedExternally 
                                ? 'text-blue-600' 
                                : 'text-emerald-600'
                          }`}>
                            {completedExternally ? 'Previously completed' : 'Completed'} 
                            {(job.score || job.external_score) && ` • Score: ${job.score || job.external_score}%`}
                            {needsImprovement && ' — Retake to improve'}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {canSwap && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSwapJob(job.id)}
                            disabled={swapping === job.id}
                          >
                            {swapping === job.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Shuffle className="h-4 w-4 mr-1" />
                                Swap
                              </>
                            )}
                          </Button>
                        )}
                        
                        {!isCompleted ? (
                          <Button
                            size="sm"
                            className="bg-gradient-to-r from-violet-500 to-purple-600"
                            onClick={() => handleStartJob(job)}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Start
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className={completedExternally ? "text-blue-600 border-blue-300" : "text-emerald-600 border-emerald-300"}
                            onClick={() => handleStartJob(job)}
                          >
                            <Target className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Job Stages */}
                    {!isCompleted && (
                      <div className="mt-4 pt-4 border-t flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <ClipboardList className="h-4 w-4" />
                          <span>Job Analysis</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Target className="h-4 w-4" />
                          <span>Candidate Screening</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Award className="h-4 w-4" />
                          <span>Final Assessment</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Actions for Admin Demo/Testing Only */}
        {user?.role === 'admin' && jobs.length > 0 && jobs.some(j => j.status !== 'completed') && (
          <Card className="border-dashed border-amber-300 bg-amber-50">
            <CardContent className="py-4">
              <p className="text-sm text-amber-700 mb-3">
                <strong>Admin Tools:</strong> Quick complete jobs for testing
              </p>
              <div className="flex flex-wrap gap-2">
                {jobs.filter(j => j.status !== 'completed').map(job => (
                  <Button
                    key={job.id}
                    size="sm"
                    variant="outline"
                    onClick={() => handleCompleteJob(job.id)}
                  >
                    Complete {job.name.substring(0, 15)}...
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default RPATJobMastery;
